import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertTriangle, X, ArrowUpRight, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface SeatUpgradeBannerProps {
  usedSeats: number;
  maxSeats: number;
}

export function SeatUpgradeBanner({ usedSeats, maxSeats }: SeatUpgradeBannerProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Check localStorage for dismissal (resets daily)
  useEffect(() => {
    const dismissedAt = localStorage.getItem('seat-upgrade-banner-dismissed');
    if (dismissedAt) {
      const dismissDate = new Date(dismissedAt);
      const now = new Date();
      // Reset if more than 24 hours ago
      if (now.getTime() - dismissDate.getTime() > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('seat-upgrade-banner-dismissed');
      } else {
        setDismissed(true);
      }
    }
  }, []);

  const percentage = (usedSeats / maxSeats) * 100;
  const remainingSeats = maxSeats - usedSeats;
  const isNearLimit = percentage >= 80;
  const isAtLimit = remainingSeats <= 0;

  // Don't show if below 80% or dismissed
  if (!isNearLimit || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('seat-upgrade-banner-dismissed', new Date().toISOString());
    toast.info('Hinweis für 24 Stunden ausgeblendet', {
      description: 'Du kannst jederzeit in den Einstellungen deinen Plan erweitern.',
    });
  };

  return (
    <div
      className={cn(
        "relative rounded-xl border p-4 mb-6 transition-all duration-300",
        isAtLimit
          ? "bg-destructive/5 border-destructive/20"
          : "bg-yellow-500/5 border-yellow-500/20"
      )}
    >
      <button
        onClick={handleDismiss}
        className={cn(
          "absolute top-3 right-3 p-1 rounded-md transition-colors",
          isAtLimit
            ? "hover:bg-destructive/10 text-destructive/60 hover:text-destructive"
            : "hover:bg-yellow-500/10 text-yellow-600/60 hover:text-yellow-600 dark:text-yellow-400/60 dark:hover:text-yellow-400"
        )}
        aria-label="Banner schließen"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-4 pr-8">
        <div
          className={cn(
            "flex-shrink-0 p-2 rounded-lg",
            isAtLimit
              ? "bg-destructive/10"
              : "bg-yellow-500/10"
          )}
        >
          {isAtLimit ? (
            <AlertTriangle className="h-5 w-5 text-destructive" />
          ) : (
            <Users className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              "font-semibold text-sm",
              isAtLimit ? "text-destructive" : "text-yellow-700 dark:text-yellow-300"
            )}
          >
            {isAtLimit
              ? "Seat-Limit erreicht"
              : `${Math.round(percentage)}% der Seats belegt`}
          </h3>
          <p
            className={cn(
              "text-sm mt-0.5",
              isAtLimit
                ? "text-destructive/80"
                : "text-yellow-600/80 dark:text-yellow-400/80"
            )}
          >
            {isAtLimit
              ? `Alle ${maxSeats} Plätze sind belegt. Erweitere deinen Plan, um weitere Teammitglieder hinzuzufügen.`
              : `${usedSeats} von ${maxSeats} Seats belegt. ${remainingSeats === 1 ? 'Nur noch 1 Platz verfügbar.' : `Noch ${remainingSeats} Plätze verfügbar.`}`}
          </p>

          <Button
            variant="outline"
            size="sm"
            className={cn(
              "mt-3 h-8 text-xs",
              isAtLimit
                ? "border-destructive/30 text-destructive hover:bg-destructive/10"
                : "border-yellow-500/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-500/10"
            )}
            onClick={() => navigate('/settings/workspace')}
          >
            <ArrowUpRight className="h-3 w-3 mr-1.5" />
            Plan upgraden
          </Button>
        </div>
      </div>
    </div>
  );
}
